import { UnifiedEvent } from "@/components/PaginatedEventsList";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { getCountryFlag, getCountryName } from "@/data/countryData";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { EventValues } from "./EventValues";

interface EventDetailsProps {
  event: UnifiedEvent | null;
  onClose: () => void;
}

const EventHeader = ({ event }: { event: UnifiedEvent }) => {
  const isMobile = useIsMobile();
  
  return (
    <CardHeader className={cn(
      "sticky top-0 z-40 bg-background/95 backdrop-blur-sm border-b",
      isMobile ? "pb-3 pt-4 px-4" : "pb-2"
    )}>
      <div className="flex items-start gap-2 pr-8">
        <div className="min-w-0 flex-1">
          <CardTitle className={cn(
            "text-base leading-6 font-medium break-words",
            isMobile && "text-lg"
          )}>
            {event.title}
          </CardTitle>
          <CardDescription className="text-sm mt-1">
            {new Date(event.date).toLocaleDateString(undefined, {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </CardDescription>
        </div>
      </div>
    </CardHeader>
  );
};

const EventMetadata = ({ event }: { event: UnifiedEvent }) => {
  const isMobile = useIsMobile();
  
  return (
    <div className={cn(
      "grid grid-cols-2 gap-4 text-sm",
      isMobile && "gap-y-6"
    )}>
      <div>
        <p className="text-sm text-muted-foreground mb-1">Country</p>
        <p className="font-medium truncate flex items-center gap-2">
          <img 
            src={getCountryFlag(event.country)} 
            alt={getCountryName(event.country)}
            className="h-4 w-4 object-cover rounded-sm"
          />
          {getCountryName(event.country)}
        </p>
      </div>
      <div>
        <p className="text-sm text-muted-foreground mb-1">Currency</p>
        <p className="font-medium truncate">{event.currency || 'USD'}</p>
      </div>
    </div>
  );
};

export const EventDetails = ({ event, onClose }: EventDetailsProps) => {
  const isMobile = useIsMobile();

  if (!event) {
    return (
      <Card className="h-full">
        <CardHeader>
          <CardTitle>Event Details</CardTitle>
          <CardDescription>Select an event to view details</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className={cn(
      "w-full relative bg-background border-0",
      isMobile && "min-h-full rounded-none"
    )}>
      <EventHeader event={event} />
      <CardContent className={cn(
        "space-y-6",
        isMobile && "p-4"
      )}>
        <EventMetadata event={event} />
        <EventValues 
          event={{
            previous: event.previous ?? 0,
            estimate: event.estimate ?? event.forecast ?? 0,
            actual: event.actual ?? 0,
            change: event.change ?? 0,
            unit: event.unit,
            impact: event.rawImpact || event.impact?.toLowerCase(),
            score: event.score
          }} 
        />
      </CardContent>
    </Card>
  );
};
